// App-wide ambient theme. Each theme recolors the shared living-universe
// background (sky gradient, floating orbs, star density) while keeping all
// content on dark glass so text stays fully readable on EVERY screen — that is
// the whole point: themes change the vibe of the background, never the legibility.
//
// "auto" follows the time-of-day sky (dawn/day/dusk/night) that the app shipped
// with. All the named themes are deliberately deep/rich (not bright washes) so
// white text and glass cards always have contrast.

export const APP_THEME_EVENT = "iw-app-theme-change";
const KEY = "isaiah-app-theme";

// Themes are per-explorer: siblings sharing a device each keep their own look.
// We scope the stored value by the active learner id (falling back to a shared
// "default" bucket when no learner is selected). Legacy un-scoped value is used
// as a one-time fallback so existing users don't lose their pick.
function _scope() {
  try {
    const l = JSON.parse(localStorage.getItem("isaiah_active_learner") || "null");
    const id = l && (l.id || l.learner_id || l.child_id || l.email);
    return id ? String(id) : "default";
  } catch { return "default"; }
}
function _scopedKey() { return `${KEY}::${_scope()}`; }

export const APP_THEMES = [
  {
    id: "auto", label: "Time of Day", desc: "Sky changes morning → night",
    swatch: "linear-gradient(135deg,#1e293b,#4338ca,#f59e0b)", accent: "#8B5CF6",
  },
  {
    id: "cosmos", label: "Cosmos", desc: "Deep space at night",
    sky: "from-[#0b1026] via-indigo-950 to-[#0a0d1f]", starOpacity: 1,
    orbColors: ["rgba(139,92,246,0.35)", "rgba(59,130,246,0.3)", "rgba(236,72,153,0.25)", "rgba(34,211,238,0.25)", "rgba(167,139,250,0.3)"],
    swatch: "linear-gradient(135deg,#0b1026,#4338ca,#8b5cf6)", accent: "#8B5CF6",
  },
  {
    id: "ocean", label: "Midnight Ocean", desc: "Deep blues & teal",
    sky: "from-[#041124] via-[#062033] to-[#03151c]", starOpacity: 0.7,
    orbColors: ["rgba(6,182,212,0.35)", "rgba(59,130,246,0.3)", "rgba(20,184,166,0.28)", "rgba(14,165,233,0.25)", "rgba(45,212,191,0.25)"],
    swatch: "linear-gradient(135deg,#041124,#0e7490,#22d3ee)", accent: "#22D3EE",
  },
  {
    id: "forest", label: "Enchanted Forest", desc: "Deep greens & fireflies",
    sky: "from-[#04160f] via-emerald-950 to-[#0a1628]", starOpacity: 0.5,
    orbColors: ["rgba(34,197,94,0.32)", "rgba(16,185,129,0.28)", "rgba(6,182,212,0.22)", "rgba(52,211,153,0.26)", "rgba(250,204,21,0.16)"],
    swatch: "linear-gradient(135deg,#04160f,#047857,#34d399)", accent: "#34D399",
  },
  {
    id: "sunset", label: "Sunset Glow", desc: "Warm purples & amber",
    sky: "from-[#1a0620] via-[#2a0e2e] to-[#1a0e05]", starOpacity: 0.6,
    orbColors: ["rgba(236,72,153,0.32)", "rgba(168,85,247,0.3)", "rgba(245,158,11,0.26)", "rgba(249,115,22,0.24)", "rgba(232,121,249,0.22)"],
    swatch: "linear-gradient(135deg,#1a0620,#a21caf,#f59e0b)", accent: "#F59E0B",
  },
  {
    id: "daylight", label: "Daylight", desc: "Soft twilight blue (brighter)",
    sky: "from-[#243b6b] via-[#2b4a7f] to-[#1c2f57]", starOpacity: 0.25,
    orbColors: ["rgba(96,165,250,0.4)", "rgba(56,189,248,0.35)", "rgba(167,139,250,0.3)", "rgba(45,212,191,0.28)", "rgba(251,191,36,0.2)"],
    swatch: "linear-gradient(135deg,#2b4a7f,#38bdf8,#a5b4fc)", accent: "#60A5FA",
  },
];

export function getAppTheme() {
  try {
    return localStorage.getItem(_scopedKey())
      || localStorage.getItem(KEY)   // legacy un-scoped fallback
      || "auto";
  } catch { return "auto"; }
}

export function getAppThemeConfig(id) {
  return APP_THEMES.find((t) => t.id === (id || getAppTheme())) || APP_THEMES[0];
}

export function setAppTheme(id) {
  const valid = APP_THEMES.some((t) => t.id === id) ? id : "auto";
  try { localStorage.setItem(_scopedKey(), valid); } catch { /* ignore */ }
  try { document.documentElement.setAttribute("data-app-theme", valid); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(APP_THEME_EVENT, { detail: valid })); } catch { /* SSR-safe */ }
  return valid;
}
